/**
 * «Запись» в форме новой задачи — для тестировщиков: записать, как
 * воспроизводится баг, и приложить запись к задаче.
 *
 * Есть только на досках «один проект на всю доску» и только в браузере,
 * который умеет захват экрана (см. canRecordScreen).
 *
 * Два способа на выбор:
 *  - только видео вкладки — средствами самого браузера, вкладка
 *    выбирается в окне Chrome;
 *  - видео, сетевые запросы и консоль — через расширение «Запись бага»
 *    (apps/extension). Странице чужие вкладки не видны, поэтому без
 *    расширения запросы не записать. Вкладка указывается нажатием на
 *    значок расширения в ней самой; к задаче уходят три файла: видео,
 *    журнал запросов .har и консоль .txt.
 */

import { Circle, Download as DownloadIcon, Puzzle, Square, Video } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button, cn, Modal, toast } from "@/shared/ui";

import {
  callExtension,
  EXTENSION_ZIP,
  type ExtensionStatus,
  onExtensionEvent,
  useExtensionStatus,
} from "./extensionBridge";
import { NetworkLog } from "./networkLog";
import { recordingStamp, tabStream, useScreenRecorder } from "./useScreenRecorder";

function clock(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
}

/** Расширение: off — не участвует; arming — ждём ответа на arm;
 *  waiting — ждём нажатия на значок во вкладке сайта; recording — пишем. */
type ExtensionPhase = "off" | "arming" | "waiting" | "recording";

interface ExtensionSession {
  log: NetworkLog;
  tab: { title: string; url: string };
  startedAt: number;
  version: string;
}

const DETACH_REASONS: Record<string, string> = {
  canceled_by_user: "На плашке отладки нажали «Отмена»",
  target_closed: "Вкладку с сайтом закрыли",
};

/** HAR и консоль — файлы задачи рядом с видео. */
function logFiles(session: ExtensionSession): File[] {
  const stamp = recordingStamp(new Date(session.startedAt));
  const har = JSON.stringify(session.log.toHar(session.tab, session.version));
  const started = new Date(session.startedAt).toLocaleString("ru-RU");
  const console = session.log.consoleText(
    `Консоль вкладки «${session.tab.title}»\nЗапись начата: ${started}`,
  );
  return [
    new File([har], `Сетевые запросы ${stamp}.har`, { type: "application/json" }),
    new File([console], `Консоль ${stamp}.txt`, { type: "text/plain" }),
  ];
}

export function RecordingControl({
  disabled,
  onRecorded,
  onActiveChange,
}: {
  disabled: boolean;
  onRecorded: (files: File[]) => void;
  /** Запись идёт — форму нельзя ни закрыть, ни отправить. */
  onActiveChange: (active: boolean) => void;
}) {
  const [choosing, setChoosing] = useState(false);
  const [phase, setPhase] = useState<ExtensionPhase>("off");
  const [stats, setStats] = useState({ requests: 0, errors: 0 });
  const [tabTitle, setTabTitle] = useState("");
  const session = useRef<ExtensionSession | null>(null);
  // Версия расширения — в журнал HAR (creator.version).
  const extensionVersion = useRef("");
  const extension = useExtensionStatus(choosing);

  const { state, start, stop } = useScreenRecorder({
    onRecorded: (video) => {
      const current = session.current;
      session.current = null;
      if (current !== null) {
        setPhase("off");
        // Видео могло закончиться само (закрыли вкладку) — отладчик тоже снимается.
        callExtension("stop").catch(() => undefined);
      }
      onRecorded(current === null ? [video] : [video, ...logFiles(current)]);
      toast.success(
        "Запись приложена к задаче",
        current === null
          ? video.name
          : `Видео, ${String(current.log.requestCount)} запросов и консоль`,
      );
    },
    onLimitReached: () =>
      toast.warning("Запись остановлена", "Достигнут предельный размер записи — 200 МБ"),
    onError: (message) => toast.warning("Не удалось записать экран", message),
  });

  const active = state.kind !== "idle" || phase !== "off";
  useEffect(() => onActiveChange(active), [active, onActiveChange]);

  const quitExtension = useCallback(() => {
    session.current = null;
    setPhase("off");
    callExtension("stop").catch(() => undefined);
  }, []);

  // События расширения — пока оно участвует в записи.
  const listening = phase !== "off";
  useEffect(() => {
    if (!listening) return;
    return onExtensionEvent((event) => {
      switch (event.type) {
        case "started": {
          setTabTitle(event.tab.title);
          session.current = {
            log: new NetworkLog(event.startedAt),
            tab: event.tab,
            startedAt: event.startedAt,
            version: extensionVersion.current,
          };
          void start(() => tabStream(event.streamId)).then((ok) => {
            if (ok) setPhase("recording");
            else quitExtension();
          });
          return;
        }
        case "cdp":
          session.current?.log.add(event);
          return;
        case "body":
          session.current?.log.addBody(event.requestId, event.body, event.base64Encoded);
          return;
        case "stopped":
          toast.info(
            "Запись остановлена",
            DETACH_REASONS[event.reason] ?? "Расширение отключилось от вкладки",
          );
          // Видео дописывается, а с ним в задачу уходит и журнал до этой минуты.
          if (session.current !== null) stop();
          else quitExtension();
          return;
        case "error":
          toast.warning("Запись бага", event.message);
          return;
      }
    });
  }, [listening, start, stop, quitExtension]);

  // Счётчики журнала на полосе записи — раз в полсекунды, а не на каждое событие.
  useEffect(() => {
    if (phase !== "recording") return;
    const timer = window.setInterval(() => {
      const log = session.current?.log;
      if (log !== undefined) setStats({ requests: log.requestCount, errors: log.errorCount });
    }, 500);
    return () => window.clearInterval(timer);
  }, [phase]);

  // Форму закрыли, пока расширение ждёт или пишет, — оно отпускает вкладку.
  useEffect(
    () => () => {
      if (session.current !== null) callExtension("stop").catch(() => undefined);
    },
    [],
  );

  const recordTab = () => {
    setChoosing(false);
    void start();
  };

  const recordWithExtension = () => {
    setChoosing(false);
    if (extension.kind === "ready") extensionVersion.current = extension.version;
    setPhase("arming");
    callExtension("arm").then(
      () => setPhase("waiting"),
      (error: unknown) => {
        setPhase("off");
        toast.warning(
          "Расширение не готово",
          error instanceof Error ? error.message : "Нет ответа от расширения",
        );
      },
    );
  };

  const stopAll = () => {
    if (session.current !== null) callExtension("stop").catch(() => undefined);
    stop();
  };

  const recording = state.kind === "recording" || state.kind === "finishing";

  return (
    <>
      {recording ? (
        <RecordingBar
          elapsedMs={state.kind === "recording" ? state.elapsedMs : null}
          bytes={state.kind === "recording" ? state.bytes : null}
          finishing={state.kind === "finishing"}
          tabTitle={phase === "recording" ? tabTitle : null}
          stats={phase === "recording" ? stats : null}
          onStop={stopAll}
        />
      ) : phase !== "off" ? (
        <WaitingBar
          onCancel={quitExtension}
          starting={phase === "arming" || state.kind === "starting"}
        />
      ) : (
        <Button
          variant="secondary"
          size="sm"
          icon={Video}
          loading={state.kind === "starting"}
          disabled={disabled}
          onClick={() => setChoosing(true)}
        >
          Запись
        </Button>
      )}

      <Modal open={choosing} onClose={() => setChoosing(false)} title="Запись бага" width="sm">
        <div className="flex flex-col gap-2.5">
          <ModeOption
            icon={Video}
            title="Только видео вкладки"
            description="Браузер спросит, какую вкладку записывать. Перейдите в неё и воспроизведите баг, затем вернитесь сюда и нажмите «Остановить запись»."
            onClick={recordTab}
          />
          <ModeOption
            icon={Puzzle}
            title="Видео и сетевые запросы"
            description="Через расширение «Запись бага»: видео вкладки, запросы с ответами и консоль браузера. Пароли и токены в журнале скрываются."
            disabled={extension.kind !== "ready"}
            onClick={recordWithExtension}
          />
          <ExtensionNote status={extension} />
        </div>
      </Modal>
    </>
  );
}

function ExtensionNote({ status }: { status: ExtensionStatus }) {
  if (status.kind === "ready") return null;
  if (status.kind === "checking") {
    return <p className="text-xs text-faint">Проверяем расширение…</p>;
  }
  return (
    <div className="flex flex-col gap-2 rounded-md bg-sunken px-3 py-2.5 text-xs text-muted">
      <p className="font-semibold text-fg">
        {status.kind === "outdated"
          ? `Установлена устаревшая версия расширения (${status.version}) — нужна новая`
          : "Расширение «Запись бага» не установлено"}
      </p>
      <ol className="list-decimal space-y-0.5 pl-4">
        <li>Скачайте архив и распакуйте его в постоянную папку.</li>
        <li>
          Откройте <code>chrome://extensions</code> и включите «Режим разработчика».
        </li>
        <li>«Загрузить распакованное расширение» → папка с manifest.json.</li>
        <li>Закрепите значок расширения на панели и обновите эту страницу.</li>
      </ol>
      <p>Права администратора не нужны. Подробности — в «Как установить.txt» в архиве.</p>
      <a
        href={EXTENSION_ZIP}
        download="bug-recorder.zip"
        className="inline-flex w-fit items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1 font-semibold text-fg hover:bg-sunken focus-visible:outline-2 focus-visible:outline-accent"
      >
        <DownloadIcon className="size-3.5" aria-hidden />
        Скачать расширение
      </a>
    </div>
  );
}

function WaitingBar({ onCancel, starting }: { onCancel: () => void; starting: boolean }) {
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 rounded-md border border-accent bg-sunken px-2.5 py-1.5 text-xs text-fg"
    >
      <Puzzle className="size-3.5 shrink-0 text-accent" aria-hidden />
      <span>
        {starting ? (
          "Подключаемся к вкладке…"
        ) : (
          <>
            Перейдите во вкладку с сайтом и нажмите значок <b>«Запись бага»</b> на панели браузера
            (или Alt+Shift+R)
          </>
        )}
      </span>
      <Button size="sm" variant="secondary" onClick={onCancel}>
        Отменить
      </Button>
    </div>
  );
}

function RecordingBar({
  elapsedMs,
  bytes,
  finishing,
  tabTitle,
  stats,
  onStop,
}: {
  elapsedMs: number | null;
  bytes: number | null;
  finishing: boolean;
  tabTitle: string | null;
  stats: { requests: number; errors: number } | null;
  onStop: () => void;
}) {
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 rounded-md border border-danger bg-danger-soft px-2.5 py-1.5 text-xs text-fg"
    >
      <Circle
        className={cn("size-3 shrink-0 fill-danger text-danger", !finishing && "animate-pulse")}
        aria-hidden
      />
      <span className="font-semibold">{finishing ? "Сохранение записи…" : "Идёт запись"}</span>
      {tabTitle !== null && <span className="max-w-48 truncate text-muted">«{tabTitle}»</span>}
      {elapsedMs !== null && bytes !== null && (
        <span className="tabular-nums text-muted">
          {clock(elapsedMs)} · {megabytes(bytes)}
        </span>
      )}
      {stats !== null && (
        <span className="tabular-nums text-muted">
          запросов: {stats.requests}
          {stats.errors > 0 && <span className="text-danger"> · ошибок: {stats.errors}</span>}
        </span>
      )}
      <Button size="sm" variant="danger" icon={Square} loading={finishing} onClick={onStop}>
        Остановить запись
      </Button>
    </div>
  );
}

function ModeOption({
  icon: Icon,
  title,
  description,
  disabled = false,
  onClick,
}: {
  icon: typeof Video;
  title: string;
  description: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex items-start gap-3 rounded-md border border-line px-3 py-2.5 text-left hover:border-accent hover:bg-sunken focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-line disabled:hover:bg-transparent"
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-fg">{title}</span>
        <span className="text-xs text-muted">{description}</span>
      </span>
    </button>
  );
}
